import { CellValueChangedEvent, ICellEditorParams } from 'ag-grid-community';
import { useCallback, useRef } from 'react';
import { showWarning } from 'src/utils/toast/toastUtil';
import { postHRM2010_R01Calculation } from '../Api_HRM2010_R01';
import {
    defaultHRM2010_R01Dto,
    HRM2010_R01DetailDto,
    HRM2010_R01HeaderDto,
} from '../Dto_HRM2010_R01';
import { useHRM2010_R01Store } from '../Store_HRM2010_R01';
import { validateHRM2010_R01Time } from '../Util_HRM2010_R01';

/** 위·아래 그리드와 Content가 같은 행별 요청 순서를 공유한다. */
const sharedRequestSequence = new Map<number, number>();

/** 편집된 행을 서버에서 계산하고 마지막 요청의 응답만 반영한다. */
export const useHRM2010_R01CellChange = () => {
    /** 행별 마지막 계산 요청 번호이다. */
    const requestSequence = useRef<Map<number, number>>(sharedRequestSequence);
    /** 서버 계산 결과를 화면 식별값과 신규 상태를 보존하며 교체한다. */
    const calculateHRM2010_R01Row = useCallback(async (
        row: HRM2010_R01HeaderDto,
        eventList: HRM2010_R01DetailDto[] | null,
    ) => {
        const sequence = (requestSequence.current.get(row.valueIndex) ?? 0) + 1;
        requestSequence.current.set(row.valueIndex, sequence);
        const state = useHRM2010_R01Store.getState();
        const response = await postHRM2010_R01Calculation({
            ...defaultHRM2010_R01Dto,
            workDt: state.HRM2010_R01PastFilter.workDt,
            hrmAttendDailyDtoList: [row],
            eventEmpNo: row.empNo,
            hrmAttendEventDtoList: eventList,
        });
        if (!response.data.successFlag || requestSequence.current.get(row.valueIndex) !== sequence) {
            return;
        }
        const current = useHRM2010_R01Store.getState().HRM2010_R01List
            .find(value => value.valueIndex === row.valueIndex);
        if (!current || current.empNo !== row.empNo || current.workDt !== row.workDt) {
            return;
        }
        useHRM2010_R01Store.getState().updateHRM2010_R01List(row.valueIndex, {
            ...current,
            ...response.data.data,
            valueIndex: current.valueIndex,
            orderIndex: current.orderIndex,
            orderIndexSeq: current.orderIndexSeq,
            inserted: current.inserted,
            isModified: true,
        });
    }, []);

    /** 위 그리드 입력을 검증하고 계산 대상 편집이면 서버 미리 계산을 요청한다. */
    const handleHeaderCellChange = useCallback((event: CellValueChangedEvent<HRM2010_R01HeaderDto>) => {
        if (!event.data || event.oldValue === event.newValue || event.node.rowPinned) return;
        const row = event.data;
        const field = event.colDef.field;
        const state = useHRM2010_R01Store.getState();
        if (field === 'startTime' || field === 'endTime') {
            const value = String(event.newValue ?? '');
            if (value && !validateHRM2010_R01Time(value)) {
                showWarning('출근·퇴근은 HH:MM 형식(00:00~23:59)으로 입력하세요.');
                state.updateHRM2010_R01List(row.valueIndex, { ...row, [field]: String(event.oldValue ?? '') });
                return;
            }
        }
        state.updateHRM2010_R01List(row.valueIndex, { ...row, isModified: true });
        if (field === 'remark') return;
        if (!row.empNo) return;
        if (field === 'shiftCd' || field === 'attendCd' || field === 'startTime' || field === 'endTime') {
            const events = state.selectedHRM2010_R01?.valueIndex === row.valueIndex
                ? state.HRM2010_R01DetailList.filter(value => (
                    !state.deletedHRM2010_R01DetailList.includes(String(value.valueIndex))
                ))
                : null;
            void calculateHRM2010_R01Row(row, events);
        }
    }, [calculateHRM2010_R01Row]);

    /** 아래 그리드의 코드·시각·사유를 반영하고 계산 가능한 입력이면 위 행을 다시 계산한다. */
    const handleDetailCellChange = useCallback((event: CellValueChangedEvent<HRM2010_R01DetailDto>) => {
        if (!event.data || event.oldValue === event.newValue || event.node.rowPinned) return;
        const row = event.data;
        const state = useHRM2010_R01Store.getState();
        if (event.colDef.field === 'eventTime' && row.eventTime && !validateHRM2010_R01Time(row.eventTime)) {
            showWarning('외출·복귀 시각은 HH:MM 형식이어야 합니다.');
            state.updateHRM2010_R01DetailList(row.valueIndex, { ...row, eventTime: String(event.oldValue ?? '') });
            return;
        }
        const code = state.attendCodeList.find(value => value.attendCd === row.attendCd);
        const next = event.colDef.field === 'attendCd' && code
            ? { ...row, attendNm: code.attendNm, attendGroup: code.attendGroup, workYn: code.workYn, isModified: true }
            : { ...row, isModified: true };
        state.updateHRM2010_R01DetailList(row.valueIndex, next);
        if (event.colDef.field === 'remark' || !next.eventTime || !state.selectedHRM2010_R01) return;
        const latest = useHRM2010_R01Store.getState();
        void calculateHRM2010_R01Row(
            state.selectedHRM2010_R01,
            latest.HRM2010_R01DetailList.filter(value => (
                !latest.deletedHRM2010_R01DetailList.includes(String(value.valueIndex))
            )),
        );
    }, [calculateHRM2010_R01Row]);

    /**
     * 위 그리드 근무조·근태 드롭다운의 선택값을 행에 반영하고 서버 미리 계산을 요청한다.
     * 공통 GridDropDownForm은 그리드 셀 값을 직접 바꾸지 않고 onChange만 호출하므로
     * onCellValueChanged가 발생하지 않아 이 함수에서 값 반영·수정 표시·계산을 함께 처리한다.
     * @param newValue 선택한 근무조코드 또는 근태코드
     * @param params 편집 중인 셀의 행·컬럼 정보
     * @returns 없음
     */
    const handleHeaderDropDownChange = useCallback((
        newValue: string,
        params: ICellEditorParams<HRM2010_R01HeaderDto>,
    ) => {
        const row = params.data;
        const field = params.colDef.field;
        // ?.: 행 노드가 없으면 undefined로 보고 합계 행이 아닌 것으로 처리한다.
        if (!row || params.node?.rowPinned || (field !== 'shiftCd' && field !== 'attendCd')) return;
        // ??: 기존 값이 null·undefined이면 빈 문자열로 비교해 같은 값 재선택은 무시한다.
        if (String(row[field] ?? '') === newValue) return;
        const state = useHRM2010_R01Store.getState();
        /** 선택값과 수정 표시를 넣은 새 행이다. 펼치기로 기존 필드를 복사한 뒤 선택한 필드만 바꾼다. */
        const next: HRM2010_R01HeaderDto = { ...row, [field]: newValue, isModified: true };
        if (field === 'shiftCd') {
            /** 바꾸기 전·후 근무조의 정시 출퇴근 정보이다. */
            const previousShift = state.workShiftList.find(value => value.shiftCd === row.shiftCd);
            const nextShift = state.workShiftList.find(value => value.shiftCd === newValue);
            /**
             * 출퇴근이 이전 근무조의 정시와 같으면 true이다.
             * 출퇴근은 실제 기록이므로 사람이 고친 시각은 보존하고, 일괄 정상출근처럼 정시 그대로인 행만 새 정시로 맞춘다.
             */
            const isScheduledTime = !!previousShift
                && row.startTime === previousShift.startTime
                && row.endTime === previousShift.endTime;
            /** 출퇴근이 모두 비어 있으면 true이다. 근무조 배정이 없어 빈 값으로 조회된 미입력 행이 여기에 해당한다. */
            const isEmptyTime = !row.startTime && !row.endTime;
            if ((isScheduledTime || isEmptyTime) && nextShift) {
                next.startTime = nextShift.startTime;
                next.endTime = nextShift.endTime;
            }
        }
        state.updateHRM2010_R01List(row.valueIndex, next);
        if (!next.empNo) return;
        /** 선택 사원 행이면 화면의 외출 기록(삭제 표시 제외)을, 아니면 null을 보내 DB 기록으로 계산한다. */
        const events = state.selectedHRM2010_R01?.valueIndex === row.valueIndex
            ? state.HRM2010_R01DetailList.filter(value => (
                !state.deletedHRM2010_R01DetailList.includes(String(value.valueIndex))
            ))
            : null;
        void calculateHRM2010_R01Row(next, events);
    }, [calculateHRM2010_R01Row]);

    /**
     * 아래 그리드 구분 드롭다운의 선택값과 근태 표시값을 행에 반영하고 위 행을 다시 계산한다.
     * @param newValue 선택한 외출·공용외출·복귀 근태코드
     * @param params 편집 중인 셀의 행·컬럼 정보
     * @returns 없음
     */
    const handleDetailDropDownChange = useCallback((
        newValue: string,
        params: ICellEditorParams<HRM2010_R01DetailDto>,
    ) => {
        const row = params.data;
        if (!row || params.node?.rowPinned || params.colDef.field !== 'attendCd') return;
        if (row.attendCd === newValue) return;
        const state = useHRM2010_R01Store.getState();
        /** 선택한 코드의 근태 마스터 행이다. 근무 처리 표시와 짝 계산에 쓴다. */
        const code = state.attendCodeList.find(value => value.attendCd === newValue);
        // 삼항 연산자: 코드를 찾으면 근태명·구분·근무 인정 여부도 함께 바꾸고, 못 찾으면 코드만 바꾼다.
        const next: HRM2010_R01DetailDto = code
            ? { ...row, attendCd: newValue,
                attendNm: code.attendNm,
                attendGroup: code.attendGroup,
                workYn: code.workYn,
                isModified: true }
            : { ...row, attendCd: newValue,
                isModified: true };
        state.updateHRM2010_R01DetailList(row.valueIndex, next);
        // 시각이 비어 있으면 짝 계산에서 제외되므로 시각 입력 후 계산한다.
        if (!next.eventTime) return;
        const latest = useHRM2010_R01Store.getState();
        if (!latest.selectedHRM2010_R01) return;
        void calculateHRM2010_R01Row(
            latest.selectedHRM2010_R01,
            latest.HRM2010_R01DetailList.filter(value => (
                !latest.deletedHRM2010_R01DetailList.includes(String(value.valueIndex))
            )),
        );
    }, [calculateHRM2010_R01Row]);

    return {
        handleHeaderCellChange,
        handleDetailCellChange,
        handleHeaderDropDownChange,
        handleDetailDropDownChange,
        calculateHRM2010_R01Row,
    };
};
